// ============================================================================
// @alloyhead/core — Feature Registry
// ============================================================================
// Runtime feature gating based on user tier. The app ships with all code
// bundled — this registry controls what's enabled at runtime based on a
// config object from the auth server.
//
// Usage:
//   const features = new FeatureRegistry({
//     tier: 'pro',
//     features: { histogram: true, webgpu: true, adjustmentLayers: true },
//     limits: { maxLayers: Infinity, maxCanvasSize: 16384 },
//   });
//
//   if (features.isEnabled('histogram')) { ... }
//   features.require('webgpu'); // throws if disabled
//   const max = features.getLimit('maxLayers'); // Infinity for pro
//
// Design principles:
//   - No dynamic code loading. Everything is bundled at build time.
//   - Feature checks are simple boolean lookups — zero overhead in hot paths.
//   - Tier can be updated at runtime (e.g., user upgrades mid-session).
//   - Unknown features default to disabled. Unknown limits default to 0.
// ============================================================================
/** Default tier — everything disabled, all limits at zero. */
export const FREE_TIER = {
    tier: 'free',
    features: {},
    limits: {},
};
/**
 * Runtime feature registry. Instantiated once per HostBridge.
 * All checks are O(1) hash lookups — safe to call in render loops.
 */
export class FeatureRegistry {
    config;
    listeners = [];
    constructor(config) {
        this.config = config ?? { ...FREE_TIER };
    }
    /** Current tier name */
    get tier() {
        return this.config.tier;
    }
    /** Full config (read-only snapshot) */
    getConfig() {
        return this.config;
    }
    /**
     * Check if a named feature is enabled for the current tier.
     * Returns false for unknown features.
     */
    isEnabled(feature) {
        return this.config.features[feature] === true;
    }
    /**
     * Get a numeric limit for the current tier.
     * Returns 0 for unknown limits (most restrictive default).
     * Use Infinity in the tier config for unlimited.
     */
    getLimit(name) {
        const val = this.config.limits[name];
        return val !== undefined ? val : 0;
    }
    /**
     * Guard — throws if the feature is disabled.
     * Use at API boundaries to prevent execution of gated code paths.
     *
     * @throws Error with a descriptive message including the feature name and tier
     */
    require(feature) {
        if (!this.isEnabled(feature)) {
            throw new Error(`Feature "${feature}" is not available on the "${this.config.tier}" tier.`);
        }
    }
    /**
     * Guard — throws if a limit would be exceeded.
     *
     * @param name - Limit name (e.g., 'maxLayers')
     * @param current - Current count (e.g., layers.length)
     * @throws Error if current >= limit
     */
    requireLimit(name, current) {
        const limit = this.getLimit(name);
        if (limit !== Infinity && current >= limit) {
            throw new Error(`Limit "${name}" reached: ${current} >= ${limit} on the "${this.config.tier}" tier.`);
        }
    }
    /**
     * Check if a limit would be exceeded (non-throwing).
     * Returns true if adding one more item is allowed.
     */
    canAdd(name, current) {
        const limit = this.getLimit(name);
        return limit === Infinity || current < limit;
    }
    /**
     * Update the tier at runtime. Fires change listeners.
     * Use when a user upgrades/downgrades mid-session.
     */
    setTier(config) {
        const oldTier = this.config.tier;
        this.config = config;
        if (oldTier !== config.tier) {
            for (const listener of this.listeners) {
                try {
                    listener(oldTier, config.tier, config);
                }
                catch { /* listener error */ }
            }
        }
    }
    /**
     * Listen for tier changes. Returns an unsubscribe function.
     */
    onTierChange(listener) {
        this.listeners.push(listener);
        return () => {
            const idx = this.listeners.indexOf(listener);
            if (idx >= 0)
                this.listeners.splice(idx, 1);
        };
    }
}
//# sourceMappingURL=registry.js.map